// DemoControlPage.jsx — Operational scenario simulation centre.
//
// Internal-only Demo Control page powered by `/api/demo-control/*`.
// Mirrors the backend gating exactly: visible only when the user is
// admin/director AND ENVIRONMENT=development OR org slug is the
// canonical demo agency. A 403 from the API simply renders the calm
// "unavailable" panel — no leaked surface in production.
//
// Visual language: matches Director Centre — deep slate panels, soft
// indigo accents, calm typography, no hacker / dev-tool aesthetic.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, AlertTriangle, ChevronLeft, Database, History,
  Layers, Network, RefreshCw, Sparkles, Users, Zap, Clock,
  Building2, TrendingUp, TrendingDown, Flame, Undo2, Briefcase,
  FileDown, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { api } from "../lib/api";
import { useAuth } from "../data/authStore";

const PANEL = {
  background: "linear-gradient(180deg, rgba(20,22,46,0.78) 0%, rgba(15,17,38,0.78) 100%)",
  border: "1px solid rgba(99,102,241,0.10)",
  backdropFilter: "blur(14px) saturate(140%)",
  WebkitBackdropFilter: "blur(14px) saturate(140%)",
  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset, 0 14px 30px -22px rgba(0,0,0,0.50)",
};

const PANEL_HOVER = {
  background: "linear-gradient(180deg, rgba(28,30,58,0.86) 0%, rgba(20,22,46,0.86) 100%)",
  border: "1px solid rgba(139,92,246,0.20)",
};

const ACTIONS = [
  {
    key: "reseed_contacts",
    title: "Reseed Demo Contacts",
    blurb: "Rebuild the premium celebrity contact cast for participant selectors, Deal Rooms and chain views.",
    Icon: Users,
    accent: "indigo",
    confirm: "Reseed the 10-persona celebrity contact cast? Existing demo contacts will be refreshed in place — no production records are touched.",
  },
  {
    key: "reseed_premium_deals",
    title: "Reseed Premium Deals",
    blurb: "Rebuild the 4 luxury sample transactions — Holland Park · Deansgate Square · Marine Terrace · Mayfair Penthouse.",
    Icon: Sparkles,
    accent: "violet",
    confirm: "Rebuild all 4 luxury sample deals? Idempotent — existing rows stay stable, only missing scenarios are created.",
  },
  {
    key: "seed_premium_inventory",
    title: "Seed Premium Operational Inventory",
    blurb: "20 curated transactions across London, Manchester, Bristol, Birmingham, Edinburgh, Bath — distributed across healthy, at-risk, searches, mortgage-delayed, ready-to-exchange and completed states.",
    Icon: Briefcase,
    accent: "violet",
    primary: true,
    confirm: "Seed 20 curated premium operational transactions? Idempotent — existing rows are preserved.",
  },
  {
    key: "generate_at_risk",
    title: "Generate At-Risk Pipeline",
    blurb: "Create 5 operational stress scenarios — unresponsive solicitor, mortgage expiry, missing AML, broken chain, stalled pack.",
    Icon: AlertTriangle,
    accent: "amber",
    confirm: "Seed the 5 at-risk operational stress scenarios? Director Centre risk lens will light up immediately.",
  },
  {
    key: "generate_healthy",
    title: "Generate Healthy Pipeline",
    blurb: "Create 4 calmly-progressing scenarios — milestones complete, mortgage approved, exchange scheduled.",
    Icon: Activity,
    accent: "emerald",
    confirm: "Seed the 4 healthy-pipeline scenarios? These will appear alongside the premium deals as the ideal-progression lens.",
  },
  {
    key: "simulate_exchange_week",
    title: "Simulate Exchange Week",
    blurb: "Inject rapid-completion activity across every active demo deal — completion pings, exchange confirmations, director alerts.",
    Icon: Zap,
    accent: "indigo",
    confirm: "Simulate a high-activity exchange week across every active demo deal? Adds calm timeline + activity-feed signal only — no records are deleted.",
  },
  {
    key: "simulate_chain_collapse",
    title: "Simulate Chain Collapse",
    blurb: "Propagate downstream chain-failure drift — confidence deterioration, linked-deal slippage, revenue-at-risk escalation.",
    Icon: Network,
    accent: "rose",
    confirm: "Apply chain-collapse drift across every active demo deal? Chain-health and exchange-probability values will be revised downward — easily reset.",
  },
  {
    key: "generate_director_story",
    title: "Generate Director Demo Story",
    blurb: "Compose the full 13-deal interconnected director walkthrough — contacts, premium deals, healthy pipeline, and at-risk scenarios in one calm pass.",
    Icon: Layers,
    accent: "violet",
    primary: true,
    confirm: "Compose the full director walkthrough? Reseeds contacts, premium deals, healthy pipeline and at-risk scenarios — ~10–15 seconds.",
  },
  {
    key: "reset_environment",
    title: "Reset Demo Environment",
    blurb: "Restore pristine baseline — purges simulated deals, reseeds the canonical contacts and premium deal scenarios.",
    Icon: RefreshCw,
    accent: "slate",
    danger: true,
    confirm: "Reset the demo environment to its pristine baseline? All generated scenarios will be purged and the canonical cast reseeded.",
  },
];

// Office-level operational scenarios — reversible deterministic
// transformations applied across the entire active inventory. A baseline
// snapshot is captured before mutation; `restore_baseline` reverts.
const OFFICE_SCENARIOS = [
  {
    key: "best_month_ever",
    title: "Best Month Ever",
    blurb: "Accelerated progression across the book — health, exchange probability and chain confidence lift visibly. Solicitors responsive, milestones complete ahead of schedule.",
    Icon: TrendingUp,
    accent: "emerald",
    confirm: "Apply 'Best Month Ever' across every active demo deal? Captures a baseline snapshot first so the mutation is fully reversible.",
  },
  {
    key: "market_slowdown",
    title: "Market Slowdown",
    blurb: "Systemic deceleration — response times slip, timelines extend, buyer hesitation flagged. Fee-at-risk visibility surfaces immediately.",
    Icon: TrendingDown,
    accent: "amber",
    confirm: "Apply 'Market Slowdown' across every active demo deal? Reversible — restores from baseline on demand.",
  },
  {
    key: "chain_crisis",
    title: "Chain Crisis",
    blurb: "Downstream chain failure propagation — confidence collapse, escalating operational alerts, rising fee risk. Heavy stress on chain intelligence.",
    Icon: Flame,
    accent: "rose",
    confirm: "Apply 'Chain Crisis' across every active demo deal? Fully reversible via baseline snapshot.",
  },
  {
    key: "exchange_week_chaos",
    title: "Exchange Week Chaos",
    blurb: "High-volume coordination week — multiple simultaneous exchanges, urgent pings, intense operational load. Busy but controlled.",
    Icon: Zap,
    accent: "indigo",
    confirm: "Apply 'Exchange Week Chaos' across every active demo deal? Reversible — restore baseline at any time.",
  },
  {
    key: "restore_baseline",
    title: "Restore Baseline",
    blurb: "Revert every active deal to its captured snapshot and purge every scenario-tagged activity. Premium inventory and seed data are preserved.",
    Icon: Undo2,
    accent: "slate",
    confirm: "Restore the captured baseline? Every active deal will revert to its pre-scenario state.",
  },
];

const ACCENT_STYLE = {
  indigo:  { ring: "rgba(99,102,241,0.30)",  glow: "rgba(99,102,241,0.18)",  text: "#a5b4fc" },
  violet:  { ring: "rgba(139,92,246,0.30)",  glow: "rgba(139,92,246,0.18)",  text: "#c4b5fd" },
  emerald: { ring: "rgba(16,185,129,0.30)",  glow: "rgba(16,185,129,0.18)",  text: "#6ee7b7" },
  amber:   { ring: "rgba(245,158,11,0.30)",  glow: "rgba(245,158,11,0.18)",  text: "#fcd34d" },
  rose:    { ring: "rgba(244,63,94,0.30)",   glow: "rgba(244,63,94,0.18)",   text: "#fda4af" },
  slate:   { ring: "rgba(148,163,184,0.28)", glow: "rgba(148,163,184,0.14)", text: "#cbd5e1" },
};

function ActionCard({ action, onRun, running }) {
  const { Icon, accent, primary, danger, title, blurb, confirm } = action;
  const a = ACCENT_STYLE[accent] || ACCENT_STYLE.indigo;
  const [hover, setHover] = useState(false);
  const handleClick = useCallback(() => {
    if (running) return;
    if (window.confirm(confirm)) onRun(action);
  }, [running, action, confirm, onRun]);

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={running}
      className={[
        "group relative text-left rounded-2xl p-5 transition-all duration-300",
        "w-full",
        running ? "opacity-60 cursor-wait" : "cursor-pointer hover:-translate-y-0.5",
      ].join(" ")}
      style={hover ? { ...PANEL, ...PANEL_HOVER, borderColor: a.ring } : PANEL}
      data-testid={`demo-action-${action.key}`}
    >
      {primary && (
        <span
          aria-hidden="true"
          className="absolute -inset-px rounded-2xl pointer-events-none"
          style={{ boxShadow: `0 0 0 1px ${a.ring} inset, 0 0 28px -4px ${a.glow}` }}
        />
      )}
      <div className="flex items-start gap-3.5 relative">
        <div
          className="shrink-0 w-10 h-10 rounded-xl grid place-items-center"
          style={{ background: a.glow, color: a.text }}
        >
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-[14.5px] font-semibold text-white tracking-tight leading-tight">
            {title}
          </h3>
          <p className="text-[12px] text-slate-400 mt-1.5 leading-relaxed">{blurb}</p>
          <div className="flex items-center gap-2 mt-4">
            <span
              className="text-[10.5px] uppercase tracking-[0.16em] font-bold"
              style={{ color: danger ? "#fca5a5" : a.text }}
            >
              {running ? "Running…" : primary ? "Compose →" : danger ? "Reset →" : "Run →"}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function EnvBadge({ env, isDemoAgency }) {
  const calm = (env === "development") || isDemoAgency;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.14em]"
      style={{
        background: calm ? "rgba(16,185,129,0.10)" : "rgba(244,63,94,0.10)",
        color: calm ? "#6ee7b7" : "#fda4af",
        border: `1px solid ${calm ? "rgba(16,185,129,0.30)" : "rgba(244,63,94,0.30)"}`,
      }}
      data-testid="demo-env-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "currentColor" }} />
      {env || "unknown"} · {isDemoAgency ? "demo agency" : "live agency"}
    </span>
  );
}

function Counter({ label, value, testid }) {
  return (
    <div className="rounded-xl p-3.5 flex flex-col gap-0.5" style={PANEL} data-testid={testid}>
      <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</span>
      <span className="font-display text-[22px] text-white font-semibold tabular-nums">{value ?? 0}</span>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function formatGBP(value) {
  const n = Number(value) || 0;
  return `£${n.toLocaleString("en-GB")}`;
}

function ScorecardTile({ label, value, hint, testid }) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-1"
      style={PANEL}
      data-testid={testid}
    >
      <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
        {label}
      </span>
      <span className="font-display text-[20px] text-white font-semibold tabular-nums leading-none mt-1">
        {value}
      </span>
      {hint && (
        <span className="text-[10.5px] text-slate-500 mt-0.5">{hint}</span>
      )}
    </div>
  );
}

function WalkthroughScorecard({ data, refreshing, onRefresh, apiBase }) {
  if (!data) {
    return (
      <div className="rounded-2xl p-5 text-center" style={PANEL} data-testid="scorecard-loading">
        <p className="text-[12px] text-slate-400">Loading walkthrough telemetry…</p>
      </div>
    );
  }
  const m = data.metrics || {};
  const isActive = !!data.active;
  const durationMin = Math.max(0, Math.round((data.duration_seconds || 0) / 60));
  return (
    <section className="space-y-3.5" data-testid="walkthrough-scorecard">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.14em]"
          style={{
            background: isActive ? "rgba(139,92,246,0.10)" : "rgba(148,163,184,0.08)",
            color: isActive ? "#c4b5fd" : "#94a3b8",
            border: `1px solid ${isActive ? "rgba(139,92,246,0.30)" : "rgba(148,163,184,0.18)"}`,
          }}
          data-testid="scorecard-state"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "currentColor" }} />
          {isActive ? "Walkthrough active" : "Steady state"}
        </span>
        {isActive && (
          <>
            <span data-testid="scorecard-duration">{durationMin} min · started {relativeTime(data.started_at)}</span>
            {data.operator?.name && <span>· {data.operator.name}</span>}
          </>
        )}
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="ml-auto text-[11px] text-slate-400 hover:text-indigo-200 transition-colors inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="scorecard-refresh"
        >
          <RefreshCw size={11} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="scorecard-metrics">
        <ScorecardTile
          label="Fee at risk recovered"
          value={formatGBP(m.fee_at_risk_recovered_gbp)}
          hint="at 1.0% standard fee assumption"
          testid="metric-fee-at-risk"
        />
        <ScorecardTile
          label="Confidence recovered"
          value={`${m.confidence_recovered_points ?? 0} pts`}
          hint="average across affected book"
          testid="metric-confidence"
        />
        <ScorecardTile
          label="Transactions stabilised"
          value={m.transactions_stabilised ?? 0}
          hint="risk band moved lower"
          testid="metric-stabilised"
        />
        <ScorecardTile
          label="Chains impacted"
          value={m.chains_impacted ?? 0}
          hint="distinct chain slugs"
          testid="metric-chains"
        />
        <ScorecardTile
          label="Exchange velocity"
          value={`${m.exchange_velocity_days ?? 0} d`}
          hint="days pulled forward"
          testid="metric-velocity"
        />
        <ScorecardTile
          label="Interventions logged"
          value={m.interventions_logged ?? 0}
          hint="operations in session window"
          testid="metric-interventions"
        />
      </div>

      {/* Narrative */}
      <div
        className="rounded-2xl p-5"
        style={PANEL}
        data-testid="scorecard-narrative"
      >
        <div className="flex items-center gap-2 mb-2.5">
          <ClipboardList size={13} strokeWidth={1.75} className="text-indigo-300" />
          <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-400">
            Operational narrative
          </span>
        </div>
        <p className="text-[12.5px] text-slate-200 leading-relaxed">
          {data.narrative}
        </p>
        <p className="text-[10.5px] text-slate-500 mt-3">
          Captured · {data.captured_at ? new Date(data.captured_at).toLocaleString("en-GB") : "—"}
          {" "}· {data.snapshots_count ?? 0} transaction
          {(data.snapshots_count ?? 0) !== 1 ? "s" : ""} touched
        </p>
      </div>

      {/* Export row */}
      <div className="flex flex-wrap items-center gap-2" data-testid="scorecard-exports">
        <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 mr-1">
          Export
        </span>
        {["txt", "json", "csv"].map((fmt) => (
          <a
            key={fmt}
            href={`${apiBase}/api/demo-control/walkthrough/export/${fmt}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-slate-300 hover:text-white hover:border-indigo-300 transition-colors"
            style={{
              background: "rgba(148,163,184,0.05)",
              border: "1px solid rgba(148,163,184,0.18)",
            }}
            data-testid={`scorecard-export-${fmt}`}
            target="_blank" rel="noopener noreferrer"
          >
            <FileDown size={12} strokeWidth={1.75} /> {fmt.toUpperCase()}
          </a>
        ))}
      </div>
    </section>
  );
}

function ActivityFeed({ operations }) {
  if (!operations || operations.length === 0) {
    return (
      <div className="rounded-2xl p-5 text-center" style={PANEL} data-testid="demo-feed-empty">
        <div className="mx-auto w-10 h-10 rounded-full grid place-items-center mb-2.5" style={{ background: "rgba(99,102,241,0.10)", color: "#a5b4fc" }}>
          <History size={16} strokeWidth={1.75} />
        </div>
        <p className="text-[12px] text-slate-400">No operations yet.</p>
        <p className="text-[10.5px] text-slate-500 mt-1">Run an action to see it appear here.</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl p-2" style={PANEL} data-testid="demo-feed">
      <ul className="divide-y divide-white/[0.04]">
        {operations.map((op, i) => (
          <li key={(op.created_at || "") + i} className="px-3 py-3" data-testid={`demo-feed-item-${i}`}>
            <div className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 mt-2 rounded-full shrink-0" style={{ background: "#a5b4fc" }} />
              <div className="flex-1 min-w-0">
                <p className="text-[12.5px] text-white leading-snug font-medium">{op.summary}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[10.5px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><Clock size={11} />{relativeTime(op.created_at)}</span>
                  <span>· {op.operator_name || op.operator_email || "system"}</span>
                  <span>· <span className="uppercase tracking-[0.12em]">{op.environment || "—"}</span></span>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DemoControlPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [runningKey, setRunningKey] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [scorecardRefreshing, setScorecardRefreshing] = useState(false);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const fetchScorecard = useCallback(async () => {
    setScorecardRefreshing(true);
    try {
      const { data } = await api.get("/api/demo-control/walkthrough/scorecard");
      setScorecard(data);
    } catch (_e) {
      // Quietly degrade — scorecard is read-only telemetry.
    } finally {
      setScorecardRefreshing(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/api/demo-control/status");
      setStatus(data.status);
      setOperations(data.operations || []);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error("Unable to load Demo Control status");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchScorecard();
  }, [fetchStatus, fetchScorecard]);

  const runAction = useCallback(async (action) => {
    setRunningKey(action.key);
    const toastId = toast.loading(`${action.title} · running…`);
    try {
      const { data } = await api.post(`/api/demo-control/actions/${action.key}`, {});
      toast.success(data?.result?.summary || `${action.title} complete`, { id: toastId });
      await fetchStatus();
      await fetchScorecard();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Action failed";
      toast.error(typeof msg === "string" ? msg : (msg.message || "Action failed"), { id: toastId });
    } finally {
      setRunningKey(null);
    }
  }, [fetchStatus, fetchScorecard]);

  const counters = status?.dataset_counts || {};
  const totalDeals = useMemo(() => (
    (counters.premium_deals || 0) + (counters.at_risk_scenarios || 0) +
    (counters.healthy_pipeline || 0) + (counters.premium_inventory || 0)
  ), [counters]);
  const officeScenarioActive = !!status?.office_scenario_active;

  if (forbidden) {
    return (
      <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#0A0D1A", color: "#fff" }} data-testid="demo-control-forbidden">
        <Sidebar />
        <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen relative">
          <div className="relative px-4 sm:px-6 lg:px-10 pt-10 pb-12 max-w-3xl">
            <div className="rounded-2xl p-7" style={PANEL}>
              <h1 className="font-display text-[22px] font-bold text-white tracking-tight">Demo Control Centre</h1>
              <p className="text-[12.5px] text-slate-400 mt-2 leading-relaxed">
                This surface is restricted to internal demo environments. ChainFlow is currently operating against a live agency context, so scenario simulation is intentionally unavailable.
              </p>
              <button type="button" onClick={() => nav("/settings")} className="mt-5 text-[12px] text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1" data-testid="demo-forbidden-back">
                <ChevronLeft size={14} /> Back to Settings
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#0A0D1A", color: "#fff" }} data-testid="demo-control-page">
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen relative">
        <span aria-hidden="true" className="absolute -top-32 left-1/4 w-[820px] h-[420px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(ellipse, rgba(139,92,246,0.10), transparent 70%)" }} />

        <div className="relative px-4 sm:px-6 lg:px-10 pt-7 pb-12 max-w-7xl">
          {/* Hero */}
          <div className="pl-12 md:pl-0 md:pr-48 mb-8" data-testid="demo-hero">
            <div className="flex items-center gap-3 mb-3 text-[10.5px] uppercase tracking-[0.18em] text-slate-500">
              <button type="button" onClick={() => nav("/settings")} className="hover:text-indigo-300 transition-colors inline-flex items-center gap-1" data-testid="demo-hero-back">
                <ChevronLeft size={12} /> Settings
              </button>
              <span>·</span>
              <span>Demo</span>
            </div>
            <h1 className="font-display text-[28px] sm:text-[34px] font-bold tracking-[-0.025em] text-white leading-none">
              Demo Control Centre
            </h1>
            <p className="text-[12.5px] text-slate-400 mt-2.5 leading-relaxed max-w-2xl">
              Operational scenario simulation for investor demos, office walkthroughs, onboarding rehearsals
              and QA. Every action is idempotent, side-effect free, and isolated from production telemetry.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {loading ? (
                <span className="text-[11px] text-slate-500">Loading environment…</span>
              ) : (
                <>
                  <EnvBadge env={status?.environment} isDemoAgency={status?.is_demo_agency} />
                  {officeScenarioActive && (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.14em]"
                      style={{
                        background: "rgba(139,92,246,0.10)",
                        color: "#c4b5fd",
                        border: "1px solid rgba(139,92,246,0.30)",
                      }}
                      data-testid="demo-baseline-active"
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "currentColor" }} />
                      Office scenario active · baseline captured
                    </span>
                  )}
                  <span className="text-[11px] text-slate-500" data-testid="demo-last-reseed">
                    Last operation · {status?.last_reseed_at ? relativeTime(status.last_reseed_at) : "—"}
                    {status?.last_reseed_action ? ` · ${status.last_reseed_action.replaceAll("_", " ")}` : ""}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Counters */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="demo-counters">
            <Counter label="Demo contacts" value={counters.demo_contacts} testid="demo-counter-contacts" />
            <Counter label="Active demo deals" value={totalDeals} testid="demo-counter-deals" />
            <Counter label="Premium inventory" value={counters.premium_inventory} testid="demo-counter-inventory" />
            <Counter label="At-risk scenarios" value={counters.at_risk_scenarios} testid="demo-counter-atrisk" />
            <Counter label="Healthy scenarios" value={counters.healthy_pipeline} testid="demo-counter-healthy" />
          </div>

          {/* Walkthrough scorecard — calm executive telemetry */}
          <section className="mb-8" data-testid="walkthrough-section">
            <h2 className="font-display text-[15px] font-semibold text-white tracking-tight mb-3 flex items-center gap-2">
              <ClipboardList size={14} strokeWidth={1.75} className="text-indigo-300" />
              Walkthrough scorecard
            </h2>
            <p className="text-[11.5px] text-slate-500 mb-4 leading-relaxed max-w-2xl">
              Observational telemetry over the active office scenario. Numbers are computed
              from captured baseline snapshots versus live deal state — no estimation,
              no projection. Restores when <span className="text-indigo-300">Restore Baseline</span> is run.
            </p>
            <WalkthroughScorecard
              data={scorecard}
              refreshing={scorecardRefreshing}
              onRefresh={fetchScorecard}
              apiBase={apiBase}
            />
          </section>

          {/* Action grid + feed */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
            <section data-testid="demo-actions">
              <h2 className="font-display text-[15px] font-semibold text-white tracking-tight mb-3 flex items-center gap-2">
                <Database size={14} strokeWidth={1.75} className="text-indigo-300" />
                Scenarios
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {ACTIONS.map((action) => (
                  <ActionCard
                    key={action.key}
                    action={action}
                    onRun={runAction}
                    running={runningKey === action.key}
                  />
                ))}
              </div>

              <h2 className="font-display text-[15px] font-semibold text-white tracking-tight mt-9 mb-1 flex items-center gap-2">
                <Building2 size={14} strokeWidth={1.75} className="text-indigo-300" />
                Office-level operational scenarios
              </h2>
              <p className="text-[11.5px] text-slate-500 mb-3 leading-relaxed max-w-2xl">
                Deterministic transformations applied across every active demo deal. Mutually
                exclusive · each scenario captures a baseline snapshot first, so every
                transformation is fully reversible via <span className="text-indigo-300">Restore Baseline</span>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5" data-testid="demo-office-scenarios">
                {OFFICE_SCENARIOS.map((action) => (
                  <ActionCard
                    key={action.key}
                    action={action}
                    onRun={runAction}
                    running={runningKey === action.key}
                  />
                ))}
              </div>
            </section>

            <aside data-testid="demo-feed-panel">
              <h2 className="font-display text-[15px] font-semibold text-white tracking-tight mb-3 flex items-center gap-2">
                <History size={14} strokeWidth={1.75} className="text-indigo-300" />
                Operational activity
              </h2>
              <ActivityFeed operations={operations} />
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}
